import { useCallback, useContext, useEffect, useRef } from 'react';
import { Context } from '../../../../context/UserContext';
import { useMRTState } from '../../../../hooks/useMRTState';
import { getMRTDefaultProps } from '../../../../helpers/mrt/getMRTDefaultProps';
import { corrigirDataColumnFilters } from '../../../../utils/funcoes';

export function useCargoGridModule({ tipo, cargoId, ativa, isMobile, listar, itens, setItens }) {
    const { parametro } = useContext(Context);
    const mrt = useMRTState({ storageKey: `Cargo.Form.${tipo}`, pageSizeDefault: Number(parametro?.paginacao) || 5 });
    const { columnFilters, columnFilterFns, globalFilter, sorting, pagination,
        setIsError, setIsLoading, setIsRefetching, setRowCount, setPagination } = mrt;
    const sequencia = useRef(0);
    const carregou = useRef(false);
    const remoto = Boolean(cargoId);

    const atualizar = useCallback(async () => {
        if (!cargoId) return;
        const atual = ++sequencia.current;
        setIsLoading(!carregou.current);
        setIsRefetching(carregou.current);
        try {
            const response = await listar({
                start: pagination.pageIndex * pagination.pageSize,
                size: pagination.pageSize,
                filters: JSON.stringify(corrigirDataColumnFilters(columnFilters) ?? []),
                filtersMode: JSON.stringify(columnFilterFns ?? {}),
                globalFilter: globalFilter ?? '',
                sorting: JSON.stringify(sorting ?? []),
                cargoId,
            });
            if (atual !== sequencia.current) return;
            const total = Number(response?.meta?.totalRowCount) || 0;
            const ultimaPagina = Math.max(0, Math.ceil(total / pagination.pageSize) - 1);
            setRowCount(total);
            setIsError(false);
            if (pagination.pageIndex > ultimaPagina) {
                setPagination((prev) => ({ ...prev, pageIndex: ultimaPagina }));
                return;
            }
            setItens(response?.data ?? []);
            carregou.current = true;
        } catch (error) {
            if (atual === sequencia.current) {
                setIsError(true);
                console.error(error);
            }
        } finally {
            if (atual === sequencia.current) {
                setIsLoading(false);
                setIsRefetching(false);
            }
        }
    }, [cargoId, listar, pagination.pageIndex, pagination.pageSize, columnFilters, columnFilterFns,
        globalFilter, sorting, setItens, setIsError, setIsLoading, setIsRefetching, setRowCount, setPagination]);

    useEffect(() => {
        if (ativa && remoto) atualizar();
        return () => { sequencia.current += 1; };
    }, [ativa, remoto, atualizar]);

    useEffect(() => {
        if (remoto) return;
        const ultimaPagina = Math.max(0, Math.ceil(itens.length / pagination.pageSize) - 1);
        if (pagination.pageIndex > ultimaPagina) {
            setPagination((prev) => ({ ...prev, pageIndex: ultimaPagina }));
        }
    }, [remoto, itens.length, pagination.pageIndex, pagination.pageSize, setPagination]);

    const props = getMRTDefaultProps({ mrt, parametro, isMobile });
    return {
        atualizar,
        props: {
            ...props,
            manualFiltering: remoto,
            manualSorting: remoto,
            manualPagination: remoto,
            rowCount: remoto ? mrt.rowCount : itens.length,
            enableRowSelection: false,
            muiTableContainerProps: { sx: { maxWidth: '100%', overflowX: 'auto' } },
            getRowId: (row, index) => row.uuid || `${tipo}-${index}`,
        },
    };
}
